/**
 * Every user-visible string on the page.
 *
 * Sourcing rule: nothing here is invented. Facts trace to the NuCraft repo:
 *   README.md                                  — pipeline timings, agents, metrics
 *   docs-public/concepts/how-it-works.md       — the generation flow, IntentContext
 *   docs-public/concepts/asset-types.md        — the seven asset types
 *   docs-public/concepts/quality-system.md     — validation targets
 *   docs-public/cli/installation.md            — install commands
 *   docs-public/quickstart-cli.md              — generate / analyze / validate
 *
 * Deliberately not stated: a crate count. The README says 27 in its header and
 * 16 in its differentiators, so the number is not reliable enough to publish.
 */

/* ── Destinations ─────────────────────────────────────────────────────────────
   No public site or dashboard exists yet, so every CTA is an in-page anchor and
   nothing ships dead. Set these and swap the hrefs when the hosts exist. */

export const DOCS_URL: string | null = null
export const DASHBOARD_URL: string | null = null

export const brand = {
  name: 'NuCraft',
  wordmark: 'nucraft',
  tagline: 'Context-intelligent visual asset generation',
  description:
    'Turns product context into production-ready visual assets, with technical and cultural validation built into the pipeline.',
}

export const nav = {
  links: [
    { label: 'Asset types', href: '#assets' },
    { label: 'How it works', href: '#pipeline' },
    { label: 'Validation', href: '#validation' },
    { label: 'Quickstart', href: '#start' },
  ],
  cta: { label: 'Start building', href: '#start' },
  logoAria: 'NuCraft home',
  menuOpenAria: 'Open menu',
  menuCloseAria: 'Close menu',
}

/* ── Hero ─────────────────────────────────────────────────────────────────────
   The visual is a brandkit assembling itself, panel by panel — which is what
   the product literally does. Distinct from a node diagram on purpose. */

export const hero = {
  eyebrow: 'NuCraft',
  title: 'Every asset, on brand, first time.',
  subtitle:
    'Give NuCraft your product context and it derives the visual language from it — then generates icons, illustrations, diagrams and whole brand systems that already match, with technical and cultural validation before anything reaches you.',
  primaryCta: { label: 'See the pipeline', href: '#pipeline' },
  ghostCta: { label: 'Browse asset types', href: '#assets' },
  canvasCaption: 'A brandkit assembling — palette, type, icons, mark and moodboard.',
}

/** Drives the hero sequence: each step lights a panel and updates the status line. */
export const heroSteps = [
  { status: 'Reading brief', panel: 'palette' },
  { status: 'Extracting visual language', panel: 'type' },
  { status: 'Generating candidates', panel: 'icons' },
  { status: 'Resolving the mark', panel: 'logo' },
  { status: 'Dual validation passed', panel: 'moodboard' },
] as const

/** Sample brandkit content shown in the hero panels. Illustrative, not real output. */
export const heroKit = {
  palette: {
    label: 'Palette',
    swatches: [
      { hex: '#5B4BD6', name: 'iris' },
      { hex: '#22D3EE', name: 'aqua' },
      { hex: '#F472B6', name: 'rose' },
      { hex: '#FBBF24', name: 'amber' },
      { hex: '#0F1020', name: 'ink' },
    ],
  },
  type: {
    label: 'Type',
    specimen: 'Aa',
    family: 'Outfit',
    scale: ['Display 48', 'Title 28', 'Body 16', 'Caption 12'],
  },
  icons: {
    label: 'Icon set',
    glyphs: ['upload', 'shield', 'spark', 'layers', 'pulse', 'route'],
  },
  logo: {
    label: 'Mark',
    variants: ['Solid', 'Outline', 'Mono'],
  },
  moodboard: {
    label: 'Moodboard',
    tiles: 4,
  },
}

/* ── The seven asset types ────────────────────────────────────────────────────
   Verbatim from docs-public/concepts/asset-types.md. */

export const assetsSection = {
  eyebrow: 'What it generates',
  title: 'Seven asset types, one visual language.',
  intro:
    'Each type is generated against the same derived brand context, so your fiftieth icon looks like it belongs with your first — consistent stroke weight, corner radius and density across the whole set.',
}

export interface AssetType {
  name: string
  use: string
  body: string
  formats: string
  animated: boolean
}

export const assetTypes: AssetType[] = [
  {
    name: 'Icons & Iconsets',
    use: 'Product UI',
    body: 'Consistent icon families that hold their style across a whole set, not just the first few.',
    formats: 'SVG · PNG',
    animated: true,
  },
  {
    name: 'Brand Illustrations',
    use: 'Marketing',
    body: 'On-brand illustration for landing pages, blog headers, empty states and onboarding.',
    formats: 'SVG · PNG · nunimation',
    animated: true,
  },
  {
    name: 'Hero Images',
    use: 'Landing pages',
    body: 'High-impact visuals for homepage heroes, feature announcements and campaign banners.',
    formats: 'PNG · WebP',
    animated: false,
  },
  {
    name: 'Technical Diagrams',
    use: 'Documentation',
    body: 'Architecture and flow diagrams styled to your brand rather than to a diagramming default.',
    formats: 'SVG · PNG',
    animated: false,
  },
  {
    name: 'Data Visualizations',
    use: 'Dashboards',
    body: 'Charts and data displays that match your identity, for reports, decks and product surfaces.',
    formats: 'SVG · PNG · nunimation',
    animated: true,
  },
  {
    name: 'Brand Systems',
    use: 'Full identity',
    body: 'A complete package — palettes, type pairings, icon and illustration style, usage guidelines.',
    formats: 'Full package',
    animated: false,
  },
  {
    name: 'Logos & Guidelines',
    use: 'Identity',
    body: 'Marks with variations, clear-space rules and usage guidance, as vector plus raster sizes.',
    formats: 'SVG · PNG',
    animated: false,
  },
]

/* ── The pipeline ─────────────────────────────────────────────────────────────
   From docs-public/concepts/how-it-works.md. */

export const pipelineSection = {
  eyebrow: 'How it works',
  title: 'Context in, production-ready assets out.',
  intro:
    'You do not need every input — even a plain prompt works. But the more context NuCraft has, the more the derived visual language actually sounds like your product.',
}

export interface PipelineStage {
  n: string
  name: string
  body: string
  detail: string
}

export const pipeline: PipelineStage[] = [
  {
    n: '01',
    name: 'You provide context',
    body: 'A PRD or brief, brand information, target geography and persona details. Any subset works.',
    detail: 'PRD · Brand · Geography · Persona',
  },
  {
    n: '02',
    name: 'The context engine derives intent',
    body: 'It builds an IntentContext — a structured description of how your assets should look and feel — rather than passing your prompt straight to a model.',
    detail: 'Personality · Verbs · Scenarios',
  },
  {
    n: '03',
    name: 'Models generate candidates',
    body: 'Multiple models run across the derived intent, routed by cost, and produce candidate assets.',
    detail: 'Multi-model routing',
  },
  {
    n: '04',
    name: 'Dual validation gates the output',
    body: 'Every candidate passes technical checks and brand and cultural checks before it is returned. What fails does not ship.',
    detail: 'Technical + brand/cultural',
  },
]

/** The three things IntentContext actually captures. */
export const intentContext = {
  label: 'IntentContext',
  items: [
    {
      name: 'Personality',
      body: 'The emotional register of the brand — trustworthy, playful, premium.',
    },
    {
      name: 'Verbs',
      body: 'Action words describing visual behaviour — introduce, emphasise, celebrate.',
    },
    {
      name: 'Scenarios',
      body: 'Where the asset is actually used — onboarding, pricing page, dashboard.',
    },
  ],
}

/* ── Validation ───────────────────────────────────────────────────────────────
   Targets from the README's validation table and quality-system.md. */

export const validationSection = {
  eyebrow: 'Validation',
  title: 'Nothing ships unvalidated.',
  intro:
    'Generation without a gate is just a suggestion. Every candidate is checked on two axes before it reaches you, and the thresholds are published rather than implied.',
}

export const guarantees = [
  { value: '95%+', label: 'Technical quality', body: 'Composition, legibility and standards compliance.' },
  { value: '95%+', label: 'Brand consistency', body: 'Alignment with the derived visual language.' },
  { value: '98%+', label: 'Cultural sensitivity', body: 'Geographic awareness, checked per market.' },
]

/* ── Performance ─────────────────────────────────────────────────────────────
   From the README's metrics table. */

export const perfSection = {
  eyebrow: 'Pipeline',
  title: 'Fast enough to iterate with.',
  note: 'Figures reported by the pipeline itself.',
}

export const perfMetrics = [
  { value: '20–30s', label: 'End-to-end pipeline', sub: 'Down from 80–100s' },
  { value: '71%', label: 'Lower API cost', sub: 'Through caching and routing' },
  { value: '40–60%', label: 'Cache hit rate', sub: 'Distributed, via Redis' },
  { value: '8', label: 'Parallel agents', sub: 'Moodboard orchestration' },
]

/* ── Quickstart ───────────────────────────────────────────────────────────────
   Real commands from docs-public/cli/installation.md and quickstart-cli.md. */

export const startSection = {
  eyebrow: 'Quickstart',
  title: 'Install it and generate something.',
  intro: 'The CLI installs from crates.io. Verified working against a modern Rust toolchain.',
  rolloutNote:
    'Package publishing and hosted API access are still being prepared. Everything above describes the pipeline as built.',
}

export const installCommand = 'cargo install nucraft-cli'

export const commands = [
  {
    comment: 'Generate your first icon',
    cmd: 'nucraft generate --type icon --prompt "secure payment confirmation" --style modern-minimal',
  },
  {
    comment: 'Check existing assets against a brand brief',
    cmd: 'nucraft analyze --input ./my-assets/ --brand-brief ./brand.json',
  },
  {
    comment: 'Re-run validation on anything you already have',
    cmd: 'nucraft validate --input ./icon.svg --checks all',
  },
]

export const prerequisites = [
  'Rust 1.75 or later, if building from source',
  'An API key for generation — analysis and generation use cloud models',
  'Linux, macOS, or Windows under WSL',
]

export const footer = {
  headline: 'Ready to stop hand-drawing assets?',
  blurb:
    'Give the pipeline your context once and it keeps producing work that already matches.',
  ctaPrimary: { label: 'See the pipeline', href: '#pipeline' },
  ctaSecondary: { label: 'Browse asset types', href: '#assets' },
  columns: [
    {
      title: 'Platform',
      links: [
        { label: 'Asset types', href: '#assets' },
        { label: 'How it works', href: '#pipeline' },
        { label: 'Validation', href: '#validation' },
        { label: 'Performance', href: '#performance' },
      ],
    },
    {
      title: 'Capabilities',
      links: [
        { label: 'Icons & iconsets', href: '#assets' },
        { label: 'Illustrations', href: '#assets' },
        { label: 'Brand systems', href: '#assets' },
        { label: 'Vectorisation', href: '#assets' },
      ],
    },
    {
      title: 'Getting started',
      links: [
        { label: 'Install', href: '#start' },
        { label: 'Commands', href: '#start' },
        { label: 'Back to top', href: '#top' },
      ],
    },
  ],
  legal: ['Privacy', 'Terms', 'Security'],
  copyright: '© 2026 NuCraft. Context-intelligent visual asset generation.',
}
